import { Text } from '@thanx/uikit/text'
import type { OrderSnapshot } from 'api/recoveryAi.types'
import type { SurfaceVariant } from 'components/orderFeedbackSurface'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React, { useState } from 'react'
import { useStyletron } from 'styletron-react'
import type { StyleObject } from 'styletron-standard'
import { merchantTheme } from 'theme'
import { currencyFormatter } from 'utilities/formatters'
import { translateEnum } from 'utilities/translateEnum'
import ReceiptModal from './ReceiptModal'

const t = buildTranslate('components.order_summary')

// The delivery methods this component knows how to localize; translateEnum
// falls back to the raw backend string for anything else. Module scope so the
// set is built once at import, not per render.
const KNOWN_DELIVERY_METHODS: ReadonlySet<string> = new Set([
  'pickup',
  'delivery',
  'dine_in',
  'curbside',
  'tableside',
  'drivethru',
])

// Per-surface promo-chip treatment. The RAI case detail (default) renders a
// solid neutral chip (grey90-on-grey20, ~10:1 — the prior spearmint50-on-
// spearmint10 failed AA); the order-rating detail redesign renders an OUTLINED
// light pill (white fill + thin grey border + grey70 text, per the mockup).
const PROMO_BY_VARIANT: Record<SurfaceVariant, StyleObject> = {
  'case-detail': {
    fontWeight: 600,
    backgroundColor: merchantTheme.colors.grey20,
    color: merchantTheme.colors.grey90,
  },
  'order-rating': {
    fontWeight: 400,
    backgroundColor: palette.white,
    border: `1px solid ${merchantTheme.colors.grey20}`,
    color: merchantTheme.colors.grey70,
  },
}

type Props = {
  order: OrderSnapshot
  // Which surface this strip renders on. The RAI case detail (default) renders
  // the promo chip as a solid semibold grey-fill chip; the order-rating detail
  // redesign renders it as a non-bold OUTLINED light pill. `order.promo` /
  // `order.taxCents` are nil-safe on BOTH surfaces — each is populated only
  // when its backend actually supplies a value, not gated by which surface
  // renders it.
  variant?: SurfaceVariant
}

// One-line order summary (order #, delivery method, item count, promo chip,
// total) plus the receipt affordance. Shared by the RecoveryAI case detail and
// the Reports order-rating detail page.

const OrderSummaryStrip = ({ order, variant = 'case-detail' }: Props) => {
  const [css] = useStyletron()
  const [open, setOpen] = useState(false)
  const promoStyle = PROMO_BY_VARIANT[variant]

  const deliveryLabel = translateEnum(
    KNOWN_DELIVERY_METHODS,
    order.deliveryMethod,
    t,
    'delivery_methods'
  )

  // Nexus can emit `discounts[]` (which already folds in the promo) alongside
  // the legacy `promo` string — suppress the chip so the discount isn't shown
  // twice. Only amountCents > 0 discounts count as "real" (mirrors
  // ReceiptModal's visibleDiscounts).
  const visibleDiscounts = (order.discounts ?? []).filter(
    discount => discount.amountCents > 0
  )
  const hasPromo =
    typeof order.promo === 'string' &&
    order.promo.length > 0 &&
    visibleDiscounts.length === 0
  // "N items" is the ordered quantity, not the number of distinct line items —
  // a 4-line order with qty 3 each reads "12 items", not "4 items". `?? 0`
  // guards a producer/legacy row that omits quantity (NaN-safe).
  const itemCount = order.items.reduce(
    (sum, item) => sum + (item.quantity ?? 0),
    0
  )

  return (
    <div
      className={css({
        backgroundColor: merchantTheme.colors.grey10,
        borderRadius: '4px',
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
      })}
    >
      <Text.BodyText4 bold tag="span">
        {t('order_heading', { number: order.orderNumber ?? t('empty') })}
      </Text.BodyText4>

      {order.deliveryMethod && (
        <Text.BodyText4 tag="span" color="grey70">
          {deliveryLabel}
        </Text.BodyText4>
      )}

      <Text.BodyText4 tag="span" color="grey70">
        {/* react-redux-i18n doesn't auto-suffix _one/_other from {count} — pick
            the key explicitly (repo convention, cf. RecoveryAI/Activity). */}
        {t(itemCount === 1 ? 'items_count_one' : 'items_count_other', {
          count: itemCount,
        })}
      </Text.BodyText4>

      {hasPromo && (
        <span
          className={css({
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            textTransform: 'uppercase',
            // Per-surface fill/weight/border/text color — see PROMO_BY_VARIANT.
            ...promoStyle,
          })}
        >
          {order.promo}
        </span>
      )}

      <Text.BodyText4 bold tag="span">
        {order.orderTotalCents !== null
          ? currencyFormatter(order.orderTotalCents / 100)
          : t('empty')}
      </Text.BodyText4>

      {/* `order.items` coalesces to [] when the snapshot tracks no line items
          (DATA-4857) — a real production state. A receipt with no line items is
          misleading, so gate the affordance (and the modal mount) on a non-empty
          list; the order total still shows in the strip above. */}
      {order.items.length > 0 && (
        <>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className={css({
              marginLeft: 'auto',
              padding: 0,
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              color: merchantTheme.colors.belizeHole50,
              fontWeight: 600,
            })}
          >
            {t('view_full_receipt')}
          </button>

          <ReceiptModal
            isOpen={open}
            onClose={() => setOpen(false)}
            order={order}
          />
        </>
      )}
    </div>
  )
}

export default OrderSummaryStrip
